import { watch, onMounted, onUnmounted, type Ref } from 'vue'

interface UseCarouselAutoplayOptions {
  enabled: Ref<boolean>
  interval: Ref<number>
  cooldown: Ref<number>
  onTick: () => void
}

export function useCarouselAutoplay(options: UseCarouselAutoplayOptions) {
  let intervalId: ReturnType<typeof setInterval> | null = null
  let cooldownTimeoutId: ReturnType<typeof setTimeout> | null = null

  function clearAll() {
    if (intervalId !== null) {
      clearInterval(intervalId)
      intervalId = null
    }
    if (cooldownTimeoutId !== null) {
      clearTimeout(cooldownTimeoutId)
      cooldownTimeoutId = null
    }
  }

  function startInterval() {
    clearAll()
    if (!options.enabled.value) return
    intervalId = setInterval(() => {
      options.onTick()
    }, options.interval.value)
  }

  function notifyInteraction() {
    clearAll()
    if (!options.enabled.value) return
    cooldownTimeoutId = setTimeout(() => {
      startInterval()
    }, options.cooldown.value)
  }

  watch(
    [options.enabled, options.interval],
    () => {
      if (options.enabled.value) {
        startInterval()
      } else {
        clearAll()
      }
    },
    { immediate: true },
  )

  function onVisibilityChange() {
    if (document.hidden) {
      clearAll()
    } else if (options.enabled.value) {
      startInterval()
    }
  }

  onMounted(() => {
    document.addEventListener('visibilitychange', onVisibilityChange)
  })

  onUnmounted(() => {
    clearAll()
    document.removeEventListener('visibilitychange', onVisibilityChange)
  })

  return { notifyInteraction }
}
